import { CSSProperties } from 'react';

/* ============================================================
   Switch — iOS-style toggle.
   ------------------------------------------------------------
   Dimensions match Apple's native UISwitch (51 × 31 pt). Knob
   slides on a 200 ms cubic-bezier transition that matches the
   real one closely enough that the toggle reads as native.
   No external dependencies, no Tailwind — same inline-style
   convention as the rest of the Intraop UI.
   ============================================================ */

interface Props {
  /** Whether the switch is on. */
  checked: boolean;
  /** Called with the new state when the user toggles. */
  onChange: (next: boolean) => void;
  /** Accessible label (read by VoiceOver). */
  label: string;
  /** Inert appearance + non-interactive when true. */
  disabled?: boolean;
}

export function Switch({ checked, onChange, label, disabled }: Props) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        ...trackStyle,
        background: checked
          ? 'var(--good)'
          : 'rgba(120, 120, 128, 0.32)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        aria-hidden
        style={{
          ...knobStyle,
          transform: `translateX(${checked ? 20 : 0}px)`,
        }}
      />
    </button>
  );
}

const trackStyle: CSSProperties = {
  flexShrink: 0,
  width: 51,
  height: 31,
  borderRadius: 999,
  border: 'none',
  padding: 2,
  display: 'flex',
  alignItems: 'center',
  transition: 'background-color 220ms ease',
  WebkitTapHighlightColor: 'transparent',
  font: 'inherit',
};

const knobStyle: CSSProperties = {
  display: 'inline-block',
  width: 27,
  height: 27,
  borderRadius: '50%',
  background: '#fff',
  boxShadow:
    '0 2px 4px rgba(0, 0, 0, 0.30), 0 1px 1px rgba(0, 0, 0, 0.15)',
  transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
};
